import {
  AnchorButton,
  Badge,
  Button,
  Card,
  CardButton,
  Conditional,
  DateInput,
  Field,
  SkeletonLoader,
  Tooltip,
} from '@payaca/ambrosia';

import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { getLocationSearch } from '@payaca/store/dispatch/dispatchActions';
import { BaseSupplierMaterialsFulfillment } from '@payaca/types/fulfillmentTypes';
import { PrelineColourVariant } from '@payaca/types/preline';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import { addDays, differenceInDays } from 'date-fns';
import { isEqual } from 'lodash-es';
import { FC, useEffect, useState, type JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import useGetFulfillmentOptionsForMaterials from '../../../../api/queries/fulfillment/useGetMaterialsFulfillment';
import useGetMyRegionPreferences from '../../../../api/queries/me/useGetMyRegionPreferences';
import useGetSupplierBranches from '../../../../api/queries/suppliers/useGetSupplierBranches';
import { SupplierBranch } from '../../../../gql/graphql';
import {
  CollectionFulfillmentOption,
  DeliveryFulfillmentOption,
  FulfillmentOption,
  getBadgeColour,
  getFromAvailabilityDate,
  getFulfillmentDate,
  isCollectionFulfillment,
  isDeliveryFulfillment,
  readableBranchName,
  sortBranches,
} from '../../../../helpers/fulfillment';
import { Props as PurchaseOrderDeliveryAddressControlProps } from '../../purchaseOrderDeliveryAddressControl/PurchaseOrderDeliveryAddressControl';
import { FormStageStage, TFormState } from '../CreatePurchaseOrderSidebar';
import { parseFulfillmentOptions } from '../fulfillment';
import { getReadableCallOffDate } from './DirectPurchaseOrderSummary';

const useShowLimitedOptions = <T extends object>(options: T[]) => {
  const [numberOfVisibleOptions, setNumberOfVisibleOptions] = useState(3);
  const visibleOptions = options.slice(0, numberOfVisibleOptions);
  const hasMoreHiddenOptions =
    visibleOptions.length && options.length > visibleOptions.length;

  return { visibleOptions, setNumberOfVisibleOptions, hasMoreHiddenOptions };
};

type ListedFulfillmentOptionsProps<T> = {
  options: T[];
  onClick: (option: T) => void;
  selectedFulfillmentOptionId?: string;
  renderCardBodyInner: (option: T) => JSX.Element;
  isLoading?: boolean;
};

const FulfillmentOptionCardSkeleton: FC = () => {
  return (
    <Card>
      <Card.Body className="flex flex-col gap-4">
        <div className="flex row justify-between">
          <SkeletonLoader.Title className="w-1/3" />
          <SkeletonLoader.Title className="w-8" />
        </div>
        <SkeletonLoader.Text className="w-1/2" />
      </Card.Body>
    </Card>
  );
};

const ListedFulfillmentOptions = <T extends FulfillmentOption>(
  props: ListedFulfillmentOptionsProps<T>
) => {
  const {
    options,
    onClick,
    selectedFulfillmentOptionId,
    renderCardBodyInner,
    isLoading,
  } = props;
  const { visibleOptions, setNumberOfVisibleOptions, hasMoreHiddenOptions } =
    useShowLimitedOptions(options);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <FulfillmentOptionCardSkeleton />
        <FulfillmentOptionCardSkeleton />
      </div>
    );
  }
  if (!visibleOptions?.length) {
    return <span>No options available</span>;
  }
  return (
    <div className="flex flex-col gap-4">
      {visibleOptions?.map((fulfillmentOption, i) => {
        return (
          <CardButton
            key={`fulfillment-option-${i}`}
            className={clsx(
              'cursor-pointer border-0 ring-1 ring-blue-500/10',
              selectedFulfillmentOptionId ===
                fulfillmentOption.fulfillmentOptionId && 'ring-2 ring-blue-500'
            )}
            onClick={() => onClick(fulfillmentOption)}
          >
            <CardButton.Body>
              {renderCardBodyInner(fulfillmentOption)}
            </CardButton.Body>
          </CardButton>
        );
      })}
      {hasMoreHiddenOptions && (
        <Button
          onClick={() => setNumberOfVisibleOptions((n) => n + 3)}
          variant={'link-inline'}
        >
          View more
        </Button>
      )}
    </div>
  );
};

const getFulfillmentBadgeTooltipText = (
  fulfillmentOption?: FulfillmentOption
) => {
  const fulfillmentDays = fulfillmentOption
    ? differenceInDays(getFulfillmentDate(fulfillmentOption), new Date())
    : null;

  if (fulfillmentOption?.isFullFulfillment && fulfillmentDays !== null) {
    if (fulfillmentDays === 0) {
      return 'Full order available today';
    } else if (fulfillmentDays < 7) {
      return 'Full order available within 7 days';
    } else {
      return 'Full order available in more than 7 days';
    }
  } else {
    if (fulfillmentOption) {
      return 'Partial order available';
    }
    return 'Order not available';
  }
};

const getFulfillmentBadgeConfig = (fulfillmentOption?: FulfillmentOption) => {
  const badgeColour: PrelineColourVariant = getBadgeColour(fulfillmentOption);
  const tooltipText = getFulfillmentBadgeTooltipText(fulfillmentOption);
  return {
    tooltipText,
    badgeColour,
  };
};

const DeliveryFulfillmentCardBodyInner: FC<DeliveryFulfillmentOption> = (
  fulfillmentOption
) => {
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const badgeConfig = getFulfillmentBadgeConfig(fulfillmentOption);
  return (
    <>
      <div className="flex justify-between">
        <h4>{readableBranchName(fulfillmentOption.branch.name)}</h4>
        <Tooltip tooltipContent={badgeConfig.tooltipText}>
          <Badge colour={badgeConfig.badgeColour} variant="soft">
            <UntitledIcon name="truck-02.3" className="h-5 w-5" />
          </Badge>
        </Tooltip>
      </div>
      <span>
        Delivery{' '}
        {getFromAvailabilityDate(
          fulfillmentOption.deliveryFromDate,
          false,
          regionPreferences?.locale
        )}
      </span>

      <FullPartialDetails
        isFullFulfillment={fulfillmentOption.isFullFulfillment}
        stock={fulfillmentOption.stock}
      />
      <ViewBranchDetailsButton branch={fulfillmentOption.branch} />
    </>
  );
};

const CollectionFulfillmentCardBodyInner: FC<CollectionFulfillmentOption> = (
  fulfillmentOption
) => {
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const badgeConfig = getFulfillmentBadgeConfig(fulfillmentOption);

  return (
    <>
      <div className="flex justify-between">
        <h4>{readableBranchName(fulfillmentOption.branch.name)}</h4>
        <Tooltip tooltipContent={badgeConfig.tooltipText}>
          <Badge colour={getBadgeColour(fulfillmentOption)} variant="soft">
            <UntitledIcon name="building-02.3" className="h-5 w-5" />
          </Badge>
        </Tooltip>
      </div>
      <span>
        Collection{' '}
        {getFromAvailabilityDate(
          fulfillmentOption.collectionFromDate,
          true,
          regionPreferences?.locale
        )}
      </span>
      <FullPartialDetails
        isFullFulfillment={fulfillmentOption.isFullFulfillment}
        stock={fulfillmentOption.stock}
      />
      <ViewBranchDetailsButton branch={fulfillmentOption.branch} />
    </>
  );
};

type FullPartialDetailsProps = {
  isFullFulfillment: boolean;
  stock: FulfillmentOption['stock'];
};
const FullPartialDetails: FC<FullPartialDetailsProps> = (props) => {
  const { isFullFulfillment, stock } = props;
  if (isFullFulfillment) {
    return (
      <div className="flex gap-2 items-center">
        <UntitledIcon name="check" className="size-4 text-green-500" />
        <span>Full order in stock</span>
      </div>
    );
  }
  return (
    <div>
      <div className="flex gap-2 items-center">
        <UntitledIcon
          name="alert-triangle.3"
          className="size-4 text-yellow-500"
        />
        <span>Order partially out of stock - unavailable Materials:</span>
      </div>
      <div className="flex flex-col ml-6 italic text-sm">
        {stock.map((s, i) => {
          if (s.isFullMaterialFulfillment) {
            return;
          }
          return (
            <span key={i}>
              {s.requestedQuantity - s.availableQuantity} x {s.material.name}
            </span>
          );
        })}
      </div>
    </div>
  );
};

type ListedBranchesProps = {
  selectedSupplierId: number;
  selectedBranchId?: string;
  selectedAddress: PurchaseOrderDeliveryAddressControlProps['value'];
  callOffDate?: Date;
  onSelectBranch: (branch: SupplierBranch) => void;
};
const ListedBranches: FC<ListedBranchesProps> = (props) => {
  const {
    selectedSupplierId,
    selectedAddress,
    callOffDate,
    selectedBranchId,
    onSelectBranch,
  } = props;
  const dispatch = useDispatch();
  const { branches, isLoading } = useGetSupplierBranches(
    selectedSupplierId.toString(),
    true
  );

  const [isGettingCoords, setIsGettingCoords] = useState(false);
  const [addressCoords, setAddressCoords] = useState<{
    latitude?: number;
    longitude?: number;
  }>({
    latitude: selectedAddress?.latitude,
    longitude: selectedAddress?.longitude,
  });

  useEffect(() => {
    // newly created address won't have coords, so fetch so we can sort branches
    if (!selectedAddress.latitude || !selectedAddress.longitude) {
      if (!selectedAddress.newDeliveryAddress) {
        return;
      }
      const stringAddress = getAddressAsString({
        line1: selectedAddress.newDeliveryAddress.line1,
        line2: selectedAddress.newDeliveryAddress.line2,
        city: selectedAddress.newDeliveryAddress.city,
        postcode: selectedAddress.newDeliveryAddress.postcode,
      });
      if (!stringAddress) {
        return;
      }
      setIsGettingCoords(true);
      dispatch(
        getLocationSearch.request({
          address: stringAddress,
          callback: (results) => {
            if (results.length) {
              const result = results[0];
              setAddressCoords({
                latitude: result.lat,
                longitude: result.long,
              });
            }
            setIsGettingCoords(false);
          },
          onErrorCallback: () => setIsGettingCoords(false),
        })
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [selectedAddress]);

  useEffect(() => {
    const differingCoords = !isEqual(
      {
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude,
      },
      addressCoords
    );
    if (differingCoords) {
      setIsGettingCoords(true);
      setAddressCoords({
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude,
      });
      setIsGettingCoords(false);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'addressCoords'
  }, [selectedAddress]);

  const sortedBranches = sortBranches(addressCoords, branches);
  const { visibleOptions, setNumberOfVisibleOptions, hasMoreHiddenOptions } =
    useShowLimitedOptions(sortedBranches);

  if (isLoading || isGettingCoords) {
    return (
      <div className="space-y-4">
        <Card>
          <Card.Body className="flex flex-col gap-4">
            <SkeletonLoader.Title className="w-1/3" />
          </Card.Body>
        </Card>
      </div>
    );
  }
  if (!visibleOptions?.length) {
    return <span>No branches available</span>;
  }
  return (
    <div className="flex flex-col gap-4">
      {visibleOptions.map((branch) => {
        return (
          <CardButton
            key={`call-off-collection-option-${branch.id}`}
            className={clsx(
              'cursor-pointer border-0 ring-1 ring-blue-500/10',
              !!callOffDate &&
                selectedBranchId === branch.id &&
                'ring-2 ring-blue-500'
            )}
            onClick={() => onSelectBranch(branch)}
          >
            <CardButton.Body>
              <h4>{readableBranchName(branch.name)}</h4>
              <ViewBranchDetailsButton branch={branch} />
            </CardButton.Body>
          </CardButton>
        );
      })}
      {hasMoreHiddenOptions && (
        <Button
          onClick={() => setNumberOfVisibleOptions((n) => n + 3)}
          variant={'link-inline'}
        >
          View more
        </Button>
      )}
    </div>
  );
};

type BaseFulfillmentOptionsProps = {
  formStage: FormStageStage;
  onSelectDelivery: (branchId?: string, branchName?: string) => void;
  onSelectCollection: (branchId: string, branchName: string) => void;
  hasSelectedAddress: boolean;
  onAction: () => void;
  selectedAddress: PurchaseOrderDeliveryAddressControlProps['value'];
};
type AsapFulfillmentOptionsProps = BaseFulfillmentOptionsProps & {
  projectId: number;
};
export const AsapFulfillmentOptions: FC<AsapFulfillmentOptionsProps> = (
  props
) => {
  const {
    formStage,
    hasSelectedAddress,
    onSelectDelivery,
    onSelectCollection,
    onAction,
    selectedAddress,
    projectId,
  } = props;

  const formMethods = useFormContext<TFormState>();
  const selectedSupplierId = formMethods.watch('supplierId');
  const selectedMaterials = formMethods.watch('materials');
  const selectedFulfillmentOptionId = formMethods.watch('fulfillmentOptionId');

  const { fulfillmentOptionsForMaterials, refetch, isLoading } =
    useGetFulfillmentOptionsForMaterials({
      input:
        selectedSupplierId &&
        (selectedAddress.deliveryAddressId ||
          selectedAddress.newDeliveryAddress) &&
        selectedMaterials.length
          ? {
              projectId: projectId.toString(),
              materials: selectedMaterials.map((m) => ({
                id: m.materialsListMaterial.material.id,
                quantity: m.selectedQuantity,
              })),
              supplierId: selectedSupplierId.toString(),
              addressId: selectedAddress.deliveryAddressId?.toString(),
              address: selectedAddress.newDeliveryAddress,
            }
          : undefined,
    });

  useEffect(() => {
    if (formStage === 'FULFILLMENT_OPTIONS') {
      void refetch();
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'refetch'
  }, [formStage, selectedAddress]);

  const fulfillmentOptions = parseFulfillmentOptions(
    fulfillmentOptionsForMaterials
  );
  const allCollectionOptions = (fulfillmentOptions || []).filter(
    isCollectionFulfillment
  );
  const allDeliveryOptions = (fulfillmentOptions || []).filter(
    isDeliveryFulfillment
  );

  const updateMaterialsToReflectChosenOption = (
    option: BaseSupplierMaterialsFulfillment
  ) => {
    if (!option.isFullFulfillment) {
      // fulfillment is not full - update materials with purchaseOrderQuantity to reflect the available qtys of materials
      const updatedMaterials = selectedMaterials.map((m) => {
        const fulfillmentMaterial = option?.stock.find(
          (s) => s.material.id === m.materialsListMaterial.material.id
        );
        return {
          ...m,
          purchaseOrderQuantity: fulfillmentMaterial?.availableQuantity || 0,
        };
      });
      formMethods.setValue('materials', updatedMaterials);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Conditional
        condition={formStage === 'FULFILLMENT_OPTIONS' && hasSelectedAddress}
      >
        <strong>Choose collection or delivery option</strong>
      </Conditional>
      <Conditional
        condition={
          formStage === 'FULFILLMENT_OPTIONS' &&
          !fulfillmentOptions?.length &&
          !isLoading &&
          hasSelectedAddress
        }
      >
        <p>
          There are no collection or delivery options available for this address
        </p>
      </Conditional>
      <Controller
        name="fulfillmentOptionId"
        control={formMethods.control}
        render={({ field: { onChange } }) => {
          return (
            <div className="flex flex-col gap-2">
              {/* Delivery options */}
              <h4>Delivery</h4>
              <ListedFulfillmentOptions<DeliveryFulfillmentOption>
                options={allDeliveryOptions}
                onClick={(option) => {
                  onChange(option.fulfillmentOptionId);
                  formMethods.setValue('fromDate', option.deliveryFromDate);
                  formMethods.setValue('toDate', option.deliveryToDate);
                  onSelectDelivery(option.branch.id, option.branch.name);
                  updateMaterialsToReflectChosenOption(option);
                  if (formStage === 'FULFILLMENT_OPTIONS') {
                    onAction();
                  }
                }}
                selectedFulfillmentOptionId={selectedFulfillmentOptionId}
                renderCardBodyInner={(fulfillmentOption) => (
                  <DeliveryFulfillmentCardBodyInner {...fulfillmentOption} />
                )}
                isLoading={isLoading}
              />

              {/* Collection options */}
              <h4>Collection</h4>
              <ListedFulfillmentOptions<CollectionFulfillmentOption>
                options={allCollectionOptions}
                onClick={(option) => {
                  onChange(option.fulfillmentOptionId);
                  formMethods.setValue('fromDate', option.collectionFromDate);
                  onSelectCollection(option.branch.id, option.branch.name);

                  updateMaterialsToReflectChosenOption(option);
                  if (formStage === 'FULFILLMENT_OPTIONS') {
                    onAction();
                  }
                }}
                selectedFulfillmentOptionId={selectedFulfillmentOptionId}
                renderCardBodyInner={(fulfillmentOption) => (
                  <CollectionFulfillmentCardBodyInner {...fulfillmentOption} />
                )}
                isLoading={isLoading}
              />
            </div>
          );
        }}
      />
    </div>
  );
};

type CallOffFulfillmentOptionsProps = BaseFulfillmentOptionsProps;
export const CallOffFulfillmentOptions: FC<CallOffFulfillmentOptionsProps> = (
  props
) => {
  const {
    formStage,
    onSelectDelivery,
    onSelectCollection,
    hasSelectedAddress,
    onAction,
    selectedAddress,
  } = props;
  const formMethods = useFormContext<TFormState>();

  const selectedSupplierId = formMethods.watch('supplierId');
  const callOffDate = formMethods.watch('callOffDate');
  const selectedDelivery = formMethods.watch('delivery');
  const selectedCollection = formMethods.watch('collection');
  const minCallOffOrderDate = addDays(new Date(), 6);

  return (
    <div className="flex flex-col gap-4">
      <Conditional
        condition={formStage === 'FULFILLMENT_OPTIONS' && hasSelectedAddress}
      >
        <Controller
          name="callOffDate"
          control={formMethods.control}
          render={({ field: { onChange, value, name } }) => {
            return (
              <Field name={name}>
                <Field.Label>Date</Field.Label>
                <DateInput
                  config={{
                    dates: { minDate: minCallOffOrderDate },
                  }}
                  value={value}
                  onChange={(change) => {
                    // we don't want a timezoned date here, we just need the date
                    const normalisedDate = toUTCDateMidnight(change);
                    onChange(normalisedDate);
                  }}
                  placeholder="Select date"
                />
              </Field>
            );
          }}
        />
        {!callOffDate && <strong>Select a delivery/collection date</strong>}
      </Conditional>
      <div className="flex flex-col gap-4">
        <Conditional condition={!!callOffDate}>
          <Conditional
            condition={
              formStage === 'FULFILLMENT_OPTIONS' && hasSelectedAddress
            }
          >
            <strong>Select collection or delivery option</strong>
          </Conditional>
          <div className="flex flex-col gap-2">
            {/* Delivery options */}
            <h4>Delivery</h4>
            <CardButton
              key={`call-off-delivery-option`}
              className={clsx(
                'cursor-pointer border-0 ring-1 ring-blue-500/10',
                !!callOffDate &&
                  !!selectedDelivery &&
                  !selectedDelivery?.branchId &&
                  'ring-2 ring-blue-500'
              )}
              onClick={() => {
                onSelectDelivery();
                if (formStage === 'FULFILLMENT_OPTIONS') {
                  onAction();
                }
              }}
            >
              <CardButton.Body>
                <span>
                  Delivery{' '}
                  {callOffDate ? (
                    <>on {getReadableCallOffDate(callOffDate)}</>
                  ) : null}
                </span>
              </CardButton.Body>
            </CardButton>
            <h4>Collection</h4>
            <div className="flex flex-col gap-4">
              <ListedBranches
                selectedSupplierId={selectedSupplierId}
                selectedAddress={selectedAddress}
                callOffDate={callOffDate}
                selectedBranchId={selectedCollection?.branchId}
                onSelectBranch={(branch) => {
                  onSelectCollection(branch.id, branch.name);
                  if (formStage === 'FULFILLMENT_OPTIONS') {
                    onAction();
                  }
                }}
              />
            </div>
          </div>
        </Conditional>
      </div>
    </div>
  );
};

type ViewBranchDetailsButtonProps = {
  branch: SupplierBranch;
};
const ViewBranchDetailsButton: FC<ViewBranchDetailsButtonProps> = (props) => {
  const { branch } = props;
  return (
    <AnchorButton
      variant={'link-inline'}
      href={
        branch.url
          ? branch.url
          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              branch.address.fullLocalAddress || ''
            )}`
      }
      size={'xs'}
      onClick={(e) => e.stopPropagation()}
      target="_blank"
      rel="noreferrer"
    >
      View branch details <UntitledIcon name="share-03" className="size-4" />
    </AnchorButton>
  );
};

/**
 * Convert a timezoned date to a UTC date at midnight
 */
const toUTCDateMidnight = (d: Date) => {
  const utcDate = new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  );
  return utcDate;
};
